import { FC, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { connect } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import Modal from "@albertsons/uds/molecule/Modal";
import TextArea from "@albertsons/uds/molecule/TextArea";
import { Button } from "@albertsons/uds/molecule/Button";
import { OctagonAlert } from "lucide-react";
import {
  AccessRequestPayload,
  User,
  UserRole,
  UserState,
} from "../../types/auth.types";
import { sendRequest } from "../../store/modules/auth";

interface UnauthorizedPageProps {
  isLoading: boolean;
  isRequestingUserAccess: boolean;
  user: User | null;
  sendRequest: (data: AccessRequestPayload) => void;
}

const UnauthorizedPage: FC<UnauthorizedPageProps> = (props) => {
  const { isLoading, isRequestingUserAccess, user, sendRequest } = props;

  const [userNote, setUserNote] = useState("");

  const { accounts } = useMsal();

  const isOpen = true;

  const closeHandler = () => {};

  const updateUserNote = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserNote(e.target.value);
  };

  const submitRequest = () => {
    const { username } = accounts[0];

    sendRequest({
      email: username,
      userNote,
      state: UserState.REQUESTED,
      role: UserRole.USER,
      comments: "",
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeHandler}
      minHeight={20}
      minWidth={20}
      width={800}
      maxWidth="calc(100vw - 40px)"
      className="bg-white overflow-hidden !rounded-lg"
    >
      {({ close }) => (
        <div data-testid="unauthorized-modal">
          <div className="flex h-12 p-2 bg-neutral-100">
            <div className="flex text-center items-center justify-center font-bold w-full px-3">
              <p>
                {user?.state === UserState.REQUESTED &&
                  "Access request submitted!"}
                {user?.state === UserState.DECLINED &&
                  "Access request declined!"}
                {user?.state === UserState.REVOKED && "Access revoked!"}
                {!user && "Access Denied!"}
              </p>
              <span
                className="ml-auto flex text-center items-center justify-center cursor-pointer"
                onClick={close}
              >
                <OctagonAlert />
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-3 p-5">
            {user?.state === UserState.REQUESTED && (
              <>
                <p>You request for access has been sent!</p>
                <p>
                  For getting access quickly, send an email to{" "}
                  <span className="font-semibold">IMPACT Support</span> at{" "}
                  <a
                    className="underline text-[#118dff]"
                    href={`mailto:IMPACT.Support@albertsons.com?subject=IMPACT mView - Requesting Access&body=Please provide me access for IMPACT mView.%0D%0ALDAP ID: ${user.email}%0D%0ANote (Optional): ${user.user_note}`}
                  >
                    IMPACT.Support@albertsons.com
                  </a>{" "}
                  with the following information:
                </p>
                <div>
                  <p>- LDAP ID</p>
                  <p>- Note (Optional)</p>
                </div>
              </>
            )}
            {user?.state === UserState.DECLINED && (
              <>
                <p>Your request for access has been declined!</p>
                <p>
                  For requesting again, please send an email to{" "}
                  <span className="font-semibold">IMPACT Support</span> at{" "}
                  <a
                    className="underline text-[#118dff]"
                    href={`mailto:IMPACT.Support@albertsons.com?subject=IMPACT mView - Requesting Access&body=Please provide me access for IMPACT mView.%0D%0ALDAP ID: ${user.email}%0D%0ANote (Required): ${user.user_note}`}
                  >
                    IMPACT.Support@albertsons.com
                  </a>{" "}
                  with the following information:
                </p>
                <div>
                  <p>- LDAP ID</p>
                  <p>- Note (Required)</p>
                </div>
              </>
            )}
            {user?.state === UserState.REVOKED && (
              <>
                <p>Your access have been revoked!</p>
                <p>
                  For requesting again, please send an email to{" "}
                  <span className="font-semibold">IMPACT Support</span> at{" "}
                  <a
                    className="underline text-[#118dff]"
                    href={`mailto:IMPACT.Support@albertsons.com?subject=IMPACT mView - Requesting Access&body=Please provide me access for IMPACT mView.%0D%0ALDAP ID: ${user.email}%0D%0ANote (Required): ${user.user_note}`}
                  >
                    IMPACT.Support@albertsons.com
                  </a>{" "}
                  with the following information:
                </p>
                <div>
                  <p>- LDAP ID</p>
                  <p>- Note (Required)</p>
                </div>
              </>
            )}
            {!user && (
              <>
                <p className="text-[18px]">
                  You don't have access for IMPACT mVIEW. Submit this form for
                  requesting access:
                </p>
                <p className="text-base font-semibold">Add a note (optional)</p>
                <TextArea
                  value={userNote}
                  onChange={updateUserNote}
                  placeholder="Your note"
                />
                <div className="flex justify-end gap-3">
                  <Button
                    data-testid="sendRequestButton"
                    onClick={submitRequest}
                    className="w-40"
                    disabled={isLoading || isRequestingUserAccess}
                  >
                    Send Request
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isLoading: state.auth.isLoading,
  isRequestingUserAccess: state.auth.isRequestingUserAccess,
  user: state.auth.user,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  sendRequest: (data: AccessRequestPayload) =>
    dispatch(sendRequest(false, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UnauthorizedPage);
